import { lazy, Suspense, useContext, useEffect, useState } from "react";
import AlertBox from "../components/AlertBox";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import { MainContext } from "../utils/MainContext";
import CustomModal from "../components/CustomModal";
import PrivateRouter from "../router/PrivateRouter";
import CompleteRegisterNotificationCard from "../components/CompleteRegisterNotificationCard";
import { LoginContext } from "../utils/Auth";
import Loading from "../components/Loading";
import * as NotificationsService from "../Modules/Notifications/Service/NotificationsService";
import ShareProfile from "../Modules/Dashboard/View/ShareProfile";

// Static imports (kritik səhifələr)
// Home, Login və NotFound artıq lazy-loaded olacaq

// Lazy-loaded components (bundle splitting üçün)
const Home = lazy(() => import("../Modules/Home/View/Home"));
const Login = lazy(() => import("../Modules/Auth/View/Login"));
const NotFound = lazy(() => import("../Modules/Additionals/View/NotFound"));
const Dashboard = lazy(() => import("../Modules/Dashboard/View/Dashboard"));
const Search = lazy(() => import("../Modules/Search/View/Search"));
const SearchResult = lazy(() => import("../Modules/Search/View/SearchResult"));
const BoxDetails = lazy(() => import("../Modules/Dashboard/View/BoxDetails"));
const CompanyPage = lazy(() => import("../Modules/Dashboard/View/CompanyPage"));
const Profile = lazy(() => import("../Modules/Profile/View/Profile"));
const SharedUserProfile = lazy(
  () => import("../Modules/Additionals/View/SharedUserProfile"),
);
const Settings = lazy(() => import("../Modules/Settings/View/Settings"));
const Register = lazy(() => import("../Modules/Auth/View/Register"));
const AuthPage = lazy(() => import("../Modules/Auth/View/AuthPage"));
const ForgotPassword = lazy(
  () => import("../Modules/Auth/View/ForgotPassword"),
);
const SendLink = lazy(() => import("../Modules/Auth/View/SendLink"));
const NewEmailFlow = lazy(
  () => import("../Modules/Additionals/View/NewEmailFlow"),
);
const RecoverPassword = lazy(
  () => import("../Modules/Auth/View/RecoverPassword"),
);
const SelectPack = lazy(() => import("../Modules/Additionals/View/SelectPack"));
const PaymentInfo = lazy(
  () => import("../Modules/UpgradePack/View/PaymentInfo"),
);
const Verification = lazy(
  () => import("../Modules/Additionals/View/Verification"),
);

// Events
const EventRegister = lazy(
  () => import("../Modules/Events/View/EventRegister"),
);
const EventLogin = lazy(() => import("../Modules/Events/View/EventLogin"));
const EventsDashboard = lazy(
  () => import("../Modules/Events/View/EventsDashboard"),
);
const EventCreate = lazy(() => import("../Modules/Events/View/EventCreate"));
const EventDetails = lazy(() => import("../Modules/Events/View/EventDetails"));

const EventRedirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace(/^\/event(\/|$)/, '/events$1');
  return <Navigate to={{ pathname: newPath, search: location.search, hash: location.hash }} replace />;
};

const Root = () => {
  const { alertBoxContent, customModalContent } = useContext(MainContext);
  const { user } = useContext(LoginContext);
  const { setNotificationCount } = useContext(MainContext);
  const path = useLocation();
  const [showAlertBox, setShowAlertBox] = useState(false);

  const notificationCount = async () => {
    try {
      const res = await NotificationsService.notificationsUnreadCount();
      console.log(res);
      setNotificationCount(res);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    let interval;
    if (user) {
      notificationCount();
      interval = setInterval(() => {
        notificationCount();
      }, 15000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [user]);

  useEffect(() => {
    if (user && !user?.onboarding_finished) {
      if (path.pathname !== "/register") {
        setShowAlertBox(true);
      } else {
        setShowAlertBox(false);
      }
    }
  }, [user, path.pathname]);

  return (
    <div className="projectRoot">
      {showAlertBox && <CompleteRegisterNotificationCard />}
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/:authType" element={<AuthPage />} />
          <Route
            path="/forgot-password/:recoveryType"
            element={<ForgotPassword />}
          />
          <Route path="/send-link" element={<SendLink />} />
          <Route path="/email-flow" element={<NewEmailFlow />} />
          <Route
            path="/recover-password/:type/:phone/:key"
            element={<RecoverPassword />}
          />
          <Route
            path="/select-pack/:userId/:profile_type"
            element={<SelectPack />}
          />
          <Route path="/share-profile" element={<ShareProfile />} />
          <Route path="/verification" element={<Verification />} />
          <Route path="/company-page/:companyID" element={<CompanyPage />} />
          <Route path="/profile/:username" element={<Profile />} />
          <Route
            path="/shared-user/:username"
            element={<SharedUserProfile />}
          />
          <Route path="/payment-info/:type" element={<PaymentInfo />} />
          <Route path="*" element={<NotFound />} />
          <Route element={<PrivateRouter />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/search" element={<Search />} />
            <Route path="/search-result" element={<SearchResult />} />
            <Route path="/box-details/:boxID" element={<BoxDetails />} />
            <Route path="/settings" element={<Settings />} />
          </Route>
          {/* Events Redirect */}
          <Route path="/event/*" element={<EventRedirect />} />

          {/* Events */}
          <Route path="/events/register" element={<EventRegister />} />
          <Route path="/events/register/:id" element={<EventRegister />} />
          <Route path="/events/login" element={<EventLogin />} />
          <Route path="/events/dashboard" element={<EventsDashboard />} />
          <Route path="/events/create" element={<EventCreate />} />
          <Route path="/events/edit/:eventId" element={<EventCreate />} />
          <Route path="/events/details/:eventId" element={<EventDetails />} />
        </Routes>
      </Suspense>
      {alertBoxContent?.isOpen && <AlertBox />}
      {customModalContent?.isOpen && <CustomModal />}
    </div>
  );
};

export default Root;
